import { Form, Select, Typography, Button } from "antd";
import type { FormInstance } from "antd";
import { useMemo } from "react";
import { IWhDivision } from "common/interface/IUser";

interface WarehouseOption {
  id?: number;
  name?: string;
}

interface Props {
  form: FormInstance;
  warehouses: WarehouseOption[];
  divisions: IWhDivision[];
}

/**
 * Normalises whatever the API returns for a user's mapped divisions
 * (either a flat list of ids or a list of `{ id }` objects) into number[].
 */
export const normalizeDivisionIds = (raw: any): number[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((d: any) => (typeof d === "object" && d !== null ? d.id : d))
    .filter((id: any) => typeof id === "number");
};

/**
 * Safety net that enforces the rule that every mapped warehouse must have at
 * least one division selected. The add-warehouse picker below is locked while
 * any warehouse is missing divisions, so this is normally a no-op — it still
 * guards against stale data coming from the API.
 */
export const pruneUnmappedWarehouses = (
  mappedWh: number[],
  mappedDivisions: number[],
  warehouses: WarehouseOption[],
  divisions: IWhDivision[]
): { mappedWh: number[]; mappedDivisions: number[]; removed: string[] } => {
  const divisionWhById = new Map<number, number>(
    divisions.map((d) => [d.id, d.whId])
  );
  const whHasDivision = new Set<number>();
  new Set(mappedDivisions).forEach((divId) => {
    const whId = divisionWhById.get(divId);
    if (whId != null) whHasDivision.add(whId);
  });

  const keptWh: number[] = [];
  const removed: string[] = [];
  (mappedWh ?? []).forEach((whId) => {
    if (whHasDivision.has(whId)) {
      keptWh.push(whId);
    } else {
      const name = warehouses.find((w) => w.id === whId)?.name;
      removed.push(name ? `${name} # ${whId}` : `Warehouse #${whId}`);
    }
  });

  const keptWhSet = new Set(keptWh);
  const keptDivisions = mappedDivisions.filter((divId) => {
    const whId = divisionWhById.get(divId);
    return whId != null && keptWhSet.has(whId);
  });

  return { mappedWh: keptWh, mappedDivisions: keptDivisions, removed };
};

/** No-op child used purely to register a hidden form field. */
const HiddenField = (_props: { value?: unknown; onChange?: unknown }) => null;

export const MappedWarehousesDivisions = ({
  form,
  warehouses,
  divisions,
}: Props) => {
  const mappedWh: number[] = Form.useWatch("mappedWh", form) ?? [];
  const mappedDivisions: number[] = Form.useWatch("mappedDivisions", form) ?? [];

  const warehouseById = useMemo(() => {
    const map = new Map<number, WarehouseOption>();
    warehouses.forEach((w) => {
      if (w.id != null) map.set(w.id, w);
    });
    return map;
  }, [warehouses]);

  const divisionsByWh = useMemo(() => {
    const map = new Map<number, IWhDivision[]>();
    divisions.forEach((d) => {
      const list = map.get(d.whId) ?? [];
      list.push(d);
      map.set(d.whId, list);
    });
    return map;
  }, [divisions]);

  const divisionById = useMemo(() => {
    const map = new Map<number, IWhDivision>();
    divisions.forEach((d) => map.set(d.id, d));
    return map;
  }, [divisions]);

  const whLabel = (whId: number) => {
    const wh = warehouseById.get(whId);
    return wh?.name ? `${wh.name} # ${whId}` : `Warehouse #${whId}`;
  };

  const divisionsForWh = (whId: number) =>
    mappedDivisions.filter((divId) => divisionById.get(divId)?.whId === whId);

  // A warehouse still needing divisions locks the picker until it is completed.
  const hasIncomplete = mappedWh.some((whId) => divisionsForWh(whId).length === 0);

  // Only offer warehouses that (a) aren't mapped yet and (b) actually have
  // divisions, so a warehouse can never be added into a dead-end.
  const availableWarehouses = warehouses.filter(
    (w) =>
      w.id != null &&
      !mappedWh.includes(w.id) &&
      (divisionsByWh.get(w.id)?.length ?? 0) > 0
  );

  const addWarehouse = (whId: number) => {
    form.setFieldValue("mappedWh", [...mappedWh, whId]);
  };

  const setDivisionsForWh = (whId: number, vals: number[]) => {
    const others = mappedDivisions.filter(
      (divId) => divisionById.get(divId)?.whId !== whId
    );
    form.setFieldValue("mappedDivisions", [...others, ...vals]);
  };

  const removeWarehouse = (whId: number) => {
    form.setFieldValue(
      "mappedWh",
      mappedWh.filter((id) => id !== whId)
    );
    form.setFieldValue(
      "mappedDivisions",
      mappedDivisions.filter((divId) => divisionById.get(divId)?.whId !== whId)
    );
  };

  return (
    <Form.Item label="Mapped Warehouses & Divisions">
      {/* Registers the two fields with the form so initialValues apply and
          getFieldsValue() returns them. They render nothing; their values are
          driven via setFieldValue / useWatch. */}
      <Form.Item name="mappedWh" hidden noStyle>
        <HiddenField />
      </Form.Item>
      <Form.Item name="mappedDivisions" hidden noStyle>
        <HiddenField />
      </Form.Item>

      <div
        style={{
          border: "1px solid #d9d9d9",
          borderRadius: 8,
          padding: 12,
        }}
      >
      {mappedWh.map((whId) => {
        const divs = divisionsForWh(whId);
        const isMissing = divs.length === 0;
        return (
          <div key={whId} style={{ marginBottom: 16 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 4,
              }}
            >
              <Typography.Text>{whLabel(whId)}</Typography.Text>
              <Button
                type="link"
                size="small"
                danger
                style={{ padding: 0 }}
                onClick={() => removeWarehouse(whId)}
              >
                Remove
              </Button>
            </div>
            <Select
              mode="multiple"
              style={{ width: "100%" }}
              status={isMissing ? "error" : undefined}
              placeholder="Select divisions"
              value={divs}
              onChange={(vals: number[]) => setDivisionsForWh(whId, vals)}
              options={(divisionsByWh.get(whId) ?? []).map((d) => ({
                value: d.id,
                label: `${d.name} # ${d.id}`,
              }))}
            />
            {isMissing && (
              <Typography.Text
                type="danger"
                style={{ display: "block", marginTop: 4, fontSize: 12 }}
              >
                Select at least one division to keep this warehouse.
              </Typography.Text>
            )}
          </div>
        );
      })}

      <Select
        showSearch
        value={null}
        placeholder={
          hasIncomplete ? "Select divisions above first" : "Add a warehouse"
        }
        disabled={hasIncomplete}
        style={{ width: "100%" }}
        filterOption={(input, option) =>
          String(option?.label ?? "")
            .toLowerCase()
            .includes(input.toLowerCase())
        }
        onChange={(whId: number) => addWarehouse(whId)}
        options={availableWarehouses.map((w) => ({
          value: w.id,
          label: `${w.name} # ${w.id}`,
        }))}
      />
      </div>
    </Form.Item>
  );
};
